import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useDropzone } from 'react-dropzone'
import { toast } from 'sonner'
import { useState } from 'react'
import { UploadCloud, FileText, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Field } from '@/components/ui/field'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { scholarshipSchema, type ScholarshipSchema } from '@/lib/schemas'
import { useSubmitScholarship } from '@/lib/hooks'

const MAX_SIZE = 250 * 1024 * 1024

export function ScholarshipForm() {
  const form = useForm<ScholarshipSchema>({
    resolver: zodResolver(scholarshipSchema),
    mode: 'onBlur',
  })
  const submit = useSubmitScholarship()
  const [essay, setEssay] = useState<File | null>(null)
  const [recFile, setRecFile] = useState<File | null>(null)

  async function onSubmit(values: ScholarshipSchema) {
    try {
      await submit.mutateAsync({ ...values, essayName: essay?.name, recName: recFile?.name })
      toast.success('Scholarship application submitted!', {
        description: 'The scholarship committee will review and respond within 3-4 weeks.',
      })
      form.reset()
      setEssay(null)
      setRecFile(null)
    } catch (err) {
      const e = err as { message?: string }
      toast.error(e.message ?? 'Submission failed. Please try again.')
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" noValidate>
      <fieldset className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <legend className="font-display text-xl uppercase text-cosmos-green px-2">Applicant Info</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First Name" required error={form.formState.errors.firstName?.message}>
            <Input {...form.register('firstName')} />
          </Field>
          <Field label="Last Name" required error={form.formState.errors.lastName?.message}>
            <Input {...form.register('lastName')} />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Date of Birth" required error={form.formState.errors.dob?.message}>
            <Input type="date" {...form.register('dob')} />
          </Field>
          <Field label="Gender" required error={form.formState.errors.gender?.message}>
            <Controller
              control={form.control}
              name="gender"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Boy">Boy</SelectItem>
                    <SelectItem value="Girl">Girl</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field label="Phone" required error={form.formState.errors.phone?.message}>
            <Input type="tel" {...form.register('phone')} />
          </Field>
        </div>
        <Field label="Email" required error={form.formState.errors.email?.message}>
          <Input type="email" {...form.register('email')} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Address" required error={form.formState.errors.address?.message}>
            <Input {...form.register('address')} />
          </Field>
          <Field label="Country" required error={form.formState.errors.country?.message}>
            <Input {...form.register('country')} defaultValue="United States" />
          </Field>
        </div>
      </fieldset>

      <fieldset className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <legend className="font-display text-xl uppercase text-cosmos-green px-2">Academic</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Current School" required error={form.formState.errors.school?.message}>
            <Input {...form.register('school')} />
          </Field>
          <Field label="Area of Study" required error={form.formState.errors.areaOfStudy?.message}>
            <Input {...form.register('areaOfStudy')} />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Intended College" required error={form.formState.errors.intendedCollege?.message}>
            <Input {...form.register('intendedCollege')} />
          </Field>
          <Field label="Grad Year" required error={form.formState.errors.gradYear?.message}>
            <Input type="number" {...form.register('gradYear')} />
          </Field>
          <Field label="GPA" required error={form.formState.errors.gpa?.message}>
            <Input type="number" step="0.01" {...form.register('gpa')} />
          </Field>
        </div>
        <Field label="Intended Major" required error={form.formState.errors.major?.message}>
          <Input {...form.register('major')} />
        </Field>
      </fieldset>

      <fieldset className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <legend className="font-display text-xl uppercase text-cosmos-green px-2">Soccer</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Primary Position" required error={form.formState.errors.position1?.message}>
            <Input {...form.register('position1')} placeholder="Center-back" />
          </Field>
          <Field label="Secondary Position">
            <Input {...form.register('position2')} placeholder="Defensive midfielder" />
          </Field>
        </div>
        <Field label="Years Playing" required error={form.formState.errors.duration?.message}>
          <Input {...form.register('duration')} placeholder="8 years" />
        </Field>
      </fieldset>

      <fieldset className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <legend className="font-display text-xl uppercase text-cosmos-green px-2">About You</legend>
        <Field label="How did you hear about this scholarship?" required error={form.formState.errors.hearAbout?.message}>
          <Input {...form.register('hearAbout')} />
        </Field>
        <Field label="Why are you applying?" required hint="Minimum 20 characters." error={form.formState.errors.whyApply?.message}>
          <Textarea rows={5} {...form.register('whyApply')} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Household Income — Parent 1" required error={form.formState.errors.income1?.message}>
            <Input type="number" {...form.register('income1')} />
          </Field>
          <Field label="Household Income — Parent 2">
            <Input type="number" {...form.register('income2')} />
          </Field>
        </div>
        <Field label="Other financial aid received">
          <Textarea rows={3} {...form.register('otherAid')} />
        </Field>
      </fieldset>

      <fieldset className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <legend className="font-display text-xl uppercase text-cosmos-green px-2">Recommenders</legend>
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-3">
            <div className="text-sm font-bold uppercase tracking-wide">Recommender 1</div>
            <Field label="Name" required error={form.formState.errors.rec1Name?.message}><Input {...form.register('rec1Name')} /></Field>
            <Field label="Email" required error={form.formState.errors.rec1Email?.message}><Input type="email" {...form.register('rec1Email')} /></Field>
            <Field label="Phone" required error={form.formState.errors.rec1Phone?.message}><Input type="tel" {...form.register('rec1Phone')} /></Field>
          </div>
          <div className="space-y-3">
            <div className="text-sm font-bold uppercase tracking-wide">Recommender 2</div>
            <Field label="Name" required error={form.formState.errors.rec2Name?.message}><Input {...form.register('rec2Name')} /></Field>
            <Field label="Email" required error={form.formState.errors.rec2Email?.message}><Input type="email" {...form.register('rec2Email')} /></Field>
            <Field label="Phone" required error={form.formState.errors.rec2Phone?.message}><Input type="tel" {...form.register('rec2Phone')} /></Field>
          </div>
        </div>
      </fieldset>

      <fieldset className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <legend className="font-display text-xl uppercase text-cosmos-green px-2">Uploads</legend>
        <FileDrop label="Essay (PDF, ≤ 250 MB)" file={essay} setFile={setEssay} />
        <FileDrop label="Recommendation Letters (PDF, ≤ 250 MB)" file={recFile} setFile={setRecFile} />
      </fieldset>

      <fieldset className="rounded-2xl border border-border bg-card p-6 space-y-3">
        <legend className="font-display text-xl uppercase text-cosmos-green px-2">Agreements</legend>
        {[
          { name: 'agree1', label: 'The information I have provided is accurate to the best of my knowledge.' },
          { name: 'agree2', label: 'I agree to Cosmos F.C.\'s data-use and communication policy.' },
          { name: 'agree3', label: 'I understand this application does not guarantee a scholarship.' },
        ].map((a) => (
          <div key={a.name}>
            <Controller
              control={form.control}
              name={a.name as 'agree1' | 'agree2' | 'agree3'}
              render={({ field }) => (
                <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer">
                  <Checkbox checked={!!field.value} onCheckedChange={(v) => field.onChange(v === true)} />
                  <span>{a.label} <span className="text-destructive">*</span></span>
                </label>
              )}
            />
            {form.formState.errors[a.name as 'agree1' | 'agree2' | 'agree3'] && (
              <p className="text-xs text-destructive font-medium mt-1 ml-8">Required</p>
            )}
          </div>
        ))}
      </fieldset>

      <div className="flex justify-end">
        <Button type="submit" size="lg" disabled={submit.isPending}>
          {submit.isPending ? 'Submitting…' : 'Submit Scholarship Application →'}
        </Button>
      </div>
    </form>
  )
}

function FileDrop({ label, file, setFile }: { label: string; file: File | null; setFile: (f: File | null) => void }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    maxSize: MAX_SIZE,
    multiple: false,
    onDrop: (accepted, rejected) => {
      if (rejected[0]) {
        toast.error(`File rejected: ${rejected[0].errors[0]?.message ?? 'invalid file'}`)
        return
      }
      setFile(accepted[0] ?? null)
    },
  })
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">{label}</div>
      {file ? (
        <div className="flex items-center gap-3 rounded-lg border border-cosmos-green/40 bg-cosmos-green/8 p-3">
          <FileText className="h-5 w-5 text-cosmos-green" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{file.name}</div>
            <div className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
          </div>
          <button type="button" onClick={() => setFile(null)} className="grid h-8 w-8 place-items-center rounded-full hover:bg-destructive/10">
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`flex flex-col items-center gap-2 rounded-lg border-2 border-dashed p-6 cursor-pointer transition-colors ${
            isDragActive ? 'border-cosmos-green bg-cosmos-green/8' : 'border-border hover:border-cosmos-green'
          }`}
        >
          <input {...getInputProps()} />
          <UploadCloud className="h-6 w-6 text-muted-foreground" />
          <div className="text-sm text-muted-foreground">
            {isDragActive ? 'Drop the PDF here…' : 'Drop a PDF here, or click to browse'}
          </div>
        </div>
      )}
    </div>
  )
}
